import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Header from "@/components/Header";
import VerifyBanner from "@/components/VerifyBanner";
import TrialBanner from "@/components/TrialBanner";
import ConsentBanner from "@/components/ConsentBanner";

// Route-level code-splitting: each page becomes its own webpack chunk.
// Cuts the initial JS payload on the landing / public routes dramatically
// (target: 5-7s TTI → ~2-3s).
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignupPage = lazy(() => import("@/pages/SignupPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const AdminInquiriesPage = lazy(() => import("@/pages/AdminInquiriesPage"));
const Workspace = lazy(() => import("@/pages/Workspace"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const ImpressumPage = lazy(() => import("@/pages/ImpressumPage"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function FullScreenLoading() {
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background"
      data-testid="loading-screen"
    >
      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
        loading…
      </div>
    </div>
  );
}

function AppShell({ children, hideHeader }) {
  if (hideHeader) return <>{children}</>;
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <VerifyBanner />
      <TrialBanner />
      <main className="flex-1">{children}</main>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Belt-and-suspenders: if the OAuth provider ever drops the session_id at a route
  // other than /auth/callback, still intercept it before Protected routes redirect away.
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/callback") {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<FullScreenLoading />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route path="/pricing" element={<AppShell><PricingPage /></AppShell>} />
        <Route path="/contact" element={<AppShell><ContactPage /></AppShell>} />
        <Route path="/privacy" element={<AppShell><PrivacyPage /></AppShell>} />
        <Route path="/impressum" element={<AppShell><ImpressumPage /></AppShell>} />
        <Route
          path="/profile"
          element={
            <Protected>
              <AppShell><ProfilePage /></AppShell>
            </Protected>
          }
        />
        <Route
          path="/app"
          element={
            <Protected>
              <AppShell><Workspace /></AppShell>
            </Protected>
          }
        />
        <Route
          path="/story/:id"
          element={
            <Protected>
              <AppShell><Workspace /></AppShell>
            </Protected>
          }
        />
        <Route
          path="/admin/inquiries"
          element={
            <Protected>
              <AppShell><AdminInquiriesPage /></AppShell>
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ConsentBanner />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
